import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Search, MoreHorizontal, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useDealStore } from '@/store/dealStore';
import { formatDate, formatCurrency } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { DealFormDialog } from '@/components/deals/DealFormDialog';

export function DealsPage() {
  const navigate = useNavigate();
  const { deals, isLoading, fetchDeals, fetchStages, deleteDeal, setFilters, filters } = useDealStore();
  const { toast } = useToast();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchStages();
    fetchDeals();
  }, [fetchStages, fetchDeals]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setFilters({ ...filters, search: value });
  };

  useEffect(() => {
    fetchDeals();
  }, [filters, fetchDeals]);

  const handleDelete = async (id: string) => {
    try {
      await deleteDeal(id);
      toast({ title: 'Deal deleted' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete deal', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Deals</h1>
          <p className="text-muted-foreground">Manage your sales opportunities</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/deals/pipeline">
              <LayoutGrid className="h-4 w-4 mr-2" />
              Pipeline View
            </Link>
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Deal
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search deals..." value={search} onChange={(e) => handleSearch(e.target.value)} className="pl-9" />
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Deal</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Expected Close</TableHead>
              <TableHead>Assigned To</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : deals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No deals found</TableCell>
              </TableRow>
            ) : (
              deals.map((deal) => (
                <TableRow key={deal.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/deals/${deal.id}`)}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{deal.deal_number}</p>
                      <p className="text-sm text-muted-foreground">{deal.title}</p>
                    </div>
                  </TableCell>
                  <TableCell>{deal.company_name || '-'}</TableCell>
                  <TableCell>
                    <Badge style={{ backgroundColor: deal.stage_color + '20', color: deal.stage_color, borderColor: deal.stage_color }} variant="outline">
                      {deal.stage}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{formatCurrency(deal.value)}</TableCell>
                  <TableCell>{deal.expected_close_date ? formatDate(deal.expected_close_date) : '-'}</TableCell>
                  <TableCell>{deal.assigned_to_name || '-'}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/deals/${deal.id}`); }}>View Details</DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600" onClick={(e) => { e.stopPropagation(); handleDelete(deal.id); }}>Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <DealFormDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />
    </div>
  );
}
